import { useMemo } from "react"

import { startOfDay } from "date-fns"
import { toZonedTime } from "date-fns-tz"

import { useFetchTaskTableData } from "src/data/taskManagement/hooks/useFetchTaskTableData"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { GanttChart } from "src/ui/Graphs/GanttChart"
import { TGanttChartSeriesData } from "src/ui/Graphs/graphTypes"
import { LoadingScreen } from "src/ui/LoadingScreen/LoadingScreen"
import { MBadge } from "src/ui/MBadge/MBadge"
import { getStoredSelectedTimezone } from "src/utils/l10n"

export function TaskCalendarView() {
  const { clock_type } = useGetUser()
  const { t, langKeys } = useTranslate()

  const { loading, tasks, homes, error } = useFetchTaskTableData({
    limit: 100,
    offset: 0,
    sort_by: "earliest_start_date",
    sort_order: "asc",
  })

  const { timezone } = getStoredSelectedTimezone()

  const chartData: TGanttChartSeriesData[] = useMemo(() => {
    const series = homes.map((home): TGanttChartSeriesData => {
      const data = tasks
        .filter((t) => t.home_id === home.home_id)
        .map((task) => {
          const due = toZonedTime(
            new Date(task.time_window.due_date),
            timezone.tzCode
          )

          return {
            name: task.title,
            start: startOfDay(due).getTime(),
            end: due.getTime(),
          }
        })

      return { name: home.name || "", data }
    })

    return series.filter((series) => series.data.length > 0)
  }, [homes, tasks, timezone.tzCode])

  if (loading) {
    return <LoadingScreen debugInfo={false} />
  }

  if (error) {
    return (
      <MBadge color="error">
        {t(langKeys.failed_general_error_try_refreshing_page)}
      </MBadge>
    )
  }

  return (
    <GanttChart
      seriesData={chartData}
      clockType={clock_type}
      timezone={timezone.tzCode}
      showCurrentDateIndicator
    />
  )
}
